import type { OrderStatus } from '../types';
import { OWN_STOCK_LABEL, ticketPresence, type InventorySideInput } from '../lib/inventory-side';

export const STATUS_VARIANT: Record<
  OrderStatus,
  'default' | 'secondary' | 'destructive' | 'outline'
> = {
  Pending_Approval: 'secondary',
  Preparing_Cargo: 'outline',
  In_Transit: 'secondary',
  Claim_Mgmt: 'destructive',
  Complete: 'default',
  Cancelled: 'secondary',
  Draft: 'outline',
};

export function formatQuantity(qty: string | null): string {
  if (qty === null) return '—';
  const n = parseFloat(qty);
  if (Number.isNaN(n)) return '—';
  return `${n.toLocaleString(undefined, { maximumFractionDigits: 2 })} MT`;
}

export function formatMargin(margin: number | null): string {
  if (margin === null) return '—';
  return `${(margin * 100).toFixed(2)}%`;
}

export function formatCreatedAt(iso: string | null): string {
  if (iso === null) return '—';
  return iso.slice(0, 10);
}

export function formatRoute(from: string | null, to: string | null): string {
  if (from === null && to === null) return '—';
  return `${from ?? '—'} → ${to ?? '—'}`;
}

export function formatOps(primary: string | null, secondary: string | null): string {
  if (primary === null) return 'Unassigned';
  if (secondary === null) return primary;
  return `${primary} / ${secondary}`;
}

export function formatPrice(value: string | null, currency: string | null): string {
  if (value === null) return '—';
  const n = parseFloat(value);
  if (Number.isNaN(n)) return '—';
  const formatted = n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return currency ? `${formatted} ${currency}` : formatted;
}

// The matching engine writes buyer_* from the sell ticket and supplier/seller
// from the buy ticket, so `buyerName` is the customer we sell to and
// `supplierName` is the supplier we buy from. The column reads "Supplier →
// Buyer", so render the supplier first.
//
// The Own-stock substitution keys on ticket presence per side, not on which
// name is null. A two-sided Inventory order can carry a null name on a
// populated side (a data gap), and that must still print a dash, not
// "Own stock" - only the side with no ticket behind it reads as own stock.
export function formatOrderCounterparties(
  order: InventorySideInput & {
    readonly buyerName: string | null;
    readonly supplierName: string | null;
  },
): string {
  const isInventory = order.orderType === 'Inventory';
  const { buy, sell } = ticketPresence(order);
  const supplier = isInventory && !buy ? OWN_STOCK_LABEL : (order.supplierName ?? '—');
  const buyer = isInventory && !sell ? OWN_STOCK_LABEL : (order.buyerName ?? '—');
  return `${supplier} → ${buyer}`;
}
